import React, { useEffect, useMemo, useState } from 'react'
import SafeIcon from '../../../common/SafeIcon'
import { supabase, supabaseConfigError } from '../../../config/supabase'
import { useAuth } from '../../../context/AuthContext'
import CurriculumFormDialog from './CurriculumFormDialog'
import CourseManager from './CourseManager'
import LessonManager from './LessonManager'
import CurriculumImportExport from './CurriculumImportExport'
import AssignToStudents from './AssignToStudents'
import * as FiIcons from 'react-icons/fi'

const {
  FiBookOpen,
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiRefreshCw,
  FiCopy,
  FiAlertTriangle,
  FiUserCheck,
  FiLayers
} = FiIcons

const emptyCurriculum = {
  id: null,
  name: '',
  description: '',
  grade_level: '',
  subject: '',
  total_days: 180,
  subjects: []
}

const CurriculumPage = () => {
  const { userProfile } = useAuth()
  const [curricula, setCurricula] = useState([])
  const [selectedCurriculum, setSelectedCurriculum] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [formMode, setFormMode] = useState('create')
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [formError, setFormError] = useState(null)
  const [isSaving, setIsSaving] = useState(false)
  const [pendingDeletes, setPendingDeletes] = useState({})
  const [lessonManagerOpen, setLessonManagerOpen] = useState(false)
  const [selectedCourse, setSelectedCourse] = useState(null)
  const [courseManagerKey, setCourseManagerKey] = useState(0)

  const roleLabel = useMemo(() => userProfile?.role === 'teacher' ? 'Teacher' : 'Parent', [userProfile])

  useEffect(() => {
    if (userProfile) {
      fetchCurricula()
    }
  }, [userProfile])

  const fetchCurricula = async () => {
    if (supabaseConfigError) {
      setError(supabaseConfigError.message)
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)
      const { data, error: supabaseError } = await supabase
        .from('curricula')
        .select('*')
        .order('created_at', { ascending: false })

      if (supabaseError) throw supabaseError

      setCurricula(data || [])
      setSelectedCurriculum((prev) => {
        if (!prev || !data?.length) return data?.[0] || null
        return data.find((item) => item.id === prev.id) || data[0] || null
      })
    } catch (fetchError) {
      console.error('Error fetching curricula:', fetchError)
      setError('Unable to load curricula right now. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const openCreateForm = () => {
    setFormMode('create')
    setSelectedCurriculum(selectedCurriculum || emptyCurriculum)
    setFormError(null)
    setIsFormOpen(true)
  }

  const openEditForm = (curriculum) => {
    setFormMode('edit')
    setSelectedCurriculum(curriculum)
    setFormError(null)
    setIsFormOpen(true)
  }

  const buildPayload = (values) => ({
    name: values.name?.trim() || '',
    description: values.description?.trim() || '',
    grade_level: values.grade_level?.trim() || '',
    subject: values.subject?.trim() || (Array.isArray(values.subjects) && values.subjects[0] ? values.subjects[0] : ''),
    total_days: Math.min(365, Math.max(1, Number(values.total_days) || 180)),
    subjects: Array.isArray(values.subjects) ? values.subjects.filter(Boolean) : []
  })

  const handleCreate = async (values) => {
    if (supabaseConfigError) {
      setFormError(supabaseConfigError.message)
      return
    }

    const payload = buildPayload(values)
    const tempId = `temp-${Date.now()}`
    const optimisticCurriculum = { ...payload, id: tempId, created_at: new Date().toISOString() }
    const prevCurricula = curricula
    setCurricula([optimisticCurriculum, ...curricula])
    setIsSaving(true)
    setFormError(null)

    try {
      const { data, error: supabaseError } = await supabase
        .from('curricula')
        .insert([payload])
        .select()
        .single()

      if (supabaseError) throw supabaseError

      setCurricula((existing) =>
        existing.map((item) => (item.id === tempId ? data : item))
      )
      setSelectedCurriculum(data)
      setIsFormOpen(false)
    } catch (createError) {
      console.error('Error creating curriculum:', createError)
      setCurricula(prevCurricula)
      setFormError('We could not save this curriculum. Please try again.')
    } finally {
      setIsSaving(false)
    }
  }

  const handleUpdate = async (values) => {
    if (supabaseConfigError) {
      setFormError(supabaseConfigError.message)
      return
    }

    const payload = buildPayload(values)
    const prevCurricula = curricula
    setCurricula((existing) =>
      existing.map((item) => (item.id === values.id ? { ...item, ...payload, id: values.id } : item))
    )
    setIsSaving(true)
    setFormError(null)

    try {
      const { data, error: supabaseError } = await supabase
        .from('curricula')
        .update(payload)
        .eq('id', values.id)
        .select()
        .single()

      if (supabaseError) throw supabaseError

      setCurricula((existing) =>
        existing.map((item) => (item.id === values.id ? data : item))
      )
      setSelectedCurriculum(data)
      setIsFormOpen(false)
    } catch (updateError) {
      console.error('Error updating curriculum:', updateError)
      setCurricula(prevCurricula)
      setFormError('Unable to save changes. Please try again.')
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async (curriculum) => {
    if (supabaseConfigError) {
      setError(supabaseConfigError.message)
      return
    }

    const prevCurricula = curricula
    setPendingDeletes((state) => ({ ...state, [curriculum.id]: true }))
    setCurricula((existing) => existing.filter((item) => item.id !== curriculum.id))

    try {
      const { error: supabaseError } = await supabase
        .from('curricula')
        .delete()
        .eq('id', curriculum.id)

      if (supabaseError) throw supabaseError

      setSelectedCurriculum((prev) => {
        if (!prev || prev.id !== curriculum.id) return prev
        return (prevCurricula.find((item) => item.id !== curriculum.id)) || null
      })
    } catch (deleteError) {
      console.error('Error deleting curriculum:', deleteError)
      setCurricula(prevCurricula)
      setError('Unable to delete this curriculum right now.')
    } finally {
      setPendingDeletes((state) => {
        const updated = { ...state }
        delete updated[curriculum.id]
        return updated
      })
    }
  }

  const handleDuplicate = async (curriculum) => {
    if (supabaseConfigError) {
      setError(supabaseConfigError.message)
      return
    }

    const baseName = curriculum.name || 'Untitled Curriculum'
    const duplicateValues = buildPayload({
      ...curriculum,
      name: `${baseName} (Copy)`
    })
    return handleCreate(duplicateValues)
  }

  const isEmpty = !loading && curricula.length === 0

  const openLessonManager = (course) => {
    setSelectedCourse(course)
    setLessonManagerOpen(true)
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <p className="text-sm uppercase tracking-wide text-gray-500">{roleLabel} workspace</p>
          <div className="flex items-center gap-3">
            <SafeIcon icon={FiBookOpen} className="w-6 h-6 text-[#00AAFF]" />
            <h1 className="text-3xl font-bold text-gray-900">Curricula</h1>
          </div>
          <p className="text-gray-600 mt-1">Manage the programs available to families and classes.</p>
        </div>
        <button
          onClick={openCreateForm}
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#00AAFF] text-white rounded-lg hover:bg-[#0099E5] transition-colors self-start"
        >
          <SafeIcon icon={FiPlus} className="w-5 h-5" />
          New Curriculum
        </button>
      </div>

      {error && (
        <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-100 rounded-xl text-red-700">
          <SafeIcon icon={FiAlertTriangle} className="w-5 h-5 mt-0.5" />
          <div>
            <p className="font-semibold">We ran into an issue</p>
            <p className="text-sm">{error}</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <div className="flex items-center gap-2 text-gray-700 font-semibold">
                <SafeIcon icon={FiLayers} className="w-5 h-5 text-[#00AAFF]" />
                Curriculum List
              </div>
              <button
                onClick={fetchCurricula}
                className="text-[#00AAFF] hover:text-[#0099E5] inline-flex items-center gap-2 text-sm"
              >
                <SafeIcon icon={FiRefreshCw} className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>

            {loading ? (
              <div className="p-6 space-y-3">
                {[1, 2, 3].map((key) => (
                  <div key={key} className="h-20 rounded-xl bg-gray-100 animate-pulse" />
                ))}
              </div>
            ) : isEmpty ? (
              <div className="p-8 text-center text-gray-600">
                <p className="font-semibold text-lg mb-2">No curricula yet</p>
                <p className="text-sm text-gray-500">Start by creating your first curriculum.</p>
              </div>
            ) : (
              <div className="p-4 space-y-3">
                {curricula.map((curriculum) => (
                  <div
                    key={curriculum.id}
                    className={`border rounded-xl p-4 hover:border-[#00AAFF] transition-colors cursor-pointer ${
                      selectedCurriculum?.id === curriculum.id ? 'border-[#00AAFF] bg-[#00AAFF]/5' : 'border-gray-200 bg-white'
                    }`}
                    onClick={() => setSelectedCurriculum(curriculum)}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="text-lg font-semibold text-gray-900">{curriculum.name}</h3>
                          {pendingDeletes[curriculum.id] && (
                            <span className="text-xs text-gray-600 flex items-center gap-1">
                              <SafeIcon icon={FiRefreshCw} className="w-3 h-3 animate-spin" />
                              Removing...
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 line-clamp-2">{curriculum.description}</p>
                        <div className="flex items-center gap-3 mt-2 text-xs font-medium text-gray-700">
                          <span className="px-2 py-1 rounded-full bg-blue-50 text-blue-700">Grade {curriculum.grade_level}</span>
                          <span className="px-2 py-1 rounded-full bg-purple-50 text-purple-700">{curriculum.subject}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          className="p-2 rounded-lg hover:bg-gray-100 text-gray-700"
                          onClick={(e) => {
                            e.stopPropagation()
                            openEditForm(curriculum)
                          }}
                        >
                          <SafeIcon icon={FiEdit2} className="w-4 h-4" />
                        </button>
                        <button
                          className="p-2 rounded-lg hover:bg-gray-100 text-gray-700"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleDuplicate(curriculum)
                          }}
                        >
                          <SafeIcon icon={FiCopy} className="w-4 h-4" />
                        </button>
                        <button
                          className="p-2 rounded-lg hover:bg-red-50 text-red-600"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleDelete(curriculum)
                          }}
                          disabled={pendingDeletes[curriculum.id]}
                        >
                          <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm h-fit">
            <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100">
              <SafeIcon icon={FiUserCheck} className="w-5 h-5 text-[#00AAFF]" />
              <h3 className="font-semibold text-gray-800">Curriculum details</h3>
            </div>

            {selectedCurriculum ? (
              <div className="p-5 space-y-4">
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500">Name</p>
                  <p className="text-lg font-semibold text-gray-900">{selectedCurriculum.name}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500">Description</p>
                  <p className="text-sm text-gray-700 leading-relaxed">{selectedCurriculum.description}</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs uppercase tracking-wide text-gray-500">Grade level</p>
                    <p className="text-sm font-semibold text-gray-800">{selectedCurriculum.grade_level}</p>
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-wide text-gray-500">Total days</p>
                    <p className="text-sm font-semibold text-gray-800">{selectedCurriculum.total_days ?? 30} days</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-xs uppercase tracking-wide text-gray-500">Subject</p>
                    <p className="text-sm font-semibold text-gray-800">{selectedCurriculum.subject || '—'}</p>
                  </div>
                  {Array.isArray(selectedCurriculum.subjects) && selectedCurriculum.subjects.length > 0 && (
                    <div className="col-span-2">
                      <p className="text-xs uppercase tracking-wide text-gray-500">Subjects</p>
                      <p className="text-sm text-gray-700">{selectedCurriculum.subjects.join(', ')}</p>
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => openEditForm(selectedCurriculum)}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-800"
                  >
                    <SafeIcon icon={FiEdit2} className="w-4 h-4" />
                    Edit
                  </button>
                  <button
                    onClick={() => handleDuplicate(selectedCurriculum)}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-[#00AAFF] text-[#00AAFF] hover:bg-[#00AAFF]/10"
                  >
                    <SafeIcon icon={FiCopy} className="w-4 h-4" />
                    Duplicate
                  </button>
                </div>
              </div>
            ) : (
              <div className="p-8 text-center text-gray-600">
                <p className="font-semibold mb-1">Select a curriculum</p>
                <p className="text-sm text-gray-500">Choose an item from the list to view its details.</p>
              </div>
            )}
          </div>

          <AssignToStudents curriculum={selectedCurriculum} />
          {lessonManagerOpen && selectedCourse && selectedCurriculum ? (
            <LessonManager
              courseId={selectedCourse.id}
              curriculumId={selectedCurriculum.id}
              totalDays={selectedCurriculum.total_days ?? 180}
              courseName={selectedCourse.name || selectedCourse.title}
              onClose={() => {
                setLessonManagerOpen(false)
                setSelectedCourse(null)
                setCourseManagerKey((k) => k + 1)
              }}
            />
          ) : (
            <CourseManager
              key={`course-manager-${selectedCurriculum?.id}-${courseManagerKey}`}
              curriculum={selectedCurriculum}
              onManageLessons={openLessonManager}
              curriculumId={selectedCurriculum?.id}
            />
          )}
          <CurriculumImportExport
            curriculum={selectedCurriculum}
            onImported={fetchCurricula}
          />
        </div>
      </div>

      <CurriculumFormDialog
        isOpen={isFormOpen}
        mode={formMode}
        initialValues={formMode === 'edit' ? selectedCurriculum : emptyCurriculum}
        onClose={() => setIsFormOpen(false)}
        onSubmit={formMode === 'edit' ? handleUpdate : handleCreate}
        isSaving={isSaving}
        errorMessage={formError}
      />
    </div>
  )
}

export default CurriculumPage
